/**
 * Per-field before/after delta computation for the state snapshot telemetry
 * middleware (Rally US2039705, phase 3).
 *
 * Delta shape (locked in Rally Notes DESIGN LOCK):
 *
 *   Delta:  { "slice.a.b.c": { b: before, a: after }, ... }
 *   Anchor: { s: {full snapshot}, c: {b, a delta of triggering action} }
 *
 * The single-letter keys (`b`, `a`, `s`, `c`) are deliberate — they cut ~40%
 * off the serialized byte cost vs. `before/after/snapshot/changes` and matter
 * for the p99 < 1 MB per-session budget (Rally US2040391 AC-1).
 *
 * ON-THE-WIRE SEMANTICS — `undefined` vs. missing vs. `null`:
 *
 *   `computeDelta` produces JS objects where `b` (or `a`) may hold the
 *   sentinel value `undefined` to mean "the field did not exist on that
 *   side". When the delta is serialized via `JSON.stringify` (as it is for
 *   New Relic transport), `undefined`-valued keys are **dropped** from the
 *   output — this is standard JSON behavior, not a bug.
 *
 *   Consumers (Python replay CLI, NRQL analysis) MUST treat these three
 *   cases as distinct:
 *
 *     Wire encoding              Semantic meaning
 *     -----------------          ----------------
 *     { "a": 5 }                 Field was `undefined` (absent) before, `5` now
 *     { "b": 5 }                 Field was `5` before, `undefined` (absent) now
 *     { "b": null, "a": 5 }      Field was explicitly `null` before, `5` now
 *     { "b": 5, "a": null }      Field was `5` before, explicitly `null` now
 *     { "b": 5, "a": 6 }         Field changed from `5` to `6`
 *
 *   In particular: a missing `b` on the wire is NOT the same as `b: null` —
 *   the former means "the field did not exist"; the latter means "the field
 *   existed and was explicitly null". This distinction matters for replay:
 *   applying `state[path] = null` vs. `delete state[path]` produces different
 *   subsequent-delta chains, and Redux state legitimately contains both null
 *   values (e.g. `tradeIns.payoffAmount = null` after user clears) and
 *   absent keys (never populated).
 */

/**
 * Per-field before/after entry.
 *
 * Both `b` and `a` may be `undefined` in the in-memory shape to signal
 * "field did not exist on that side". Because `JSON.stringify` drops
 * `undefined`-valued keys, the wire form of such an entry omits the key
 * entirely — consumers MUST reconstruct absence from the missing key
 * rather than treating it as `null`. See the module-level `ON-THE-WIRE
 * SEMANTICS` note for the full truth table.
 */
export type FieldDelta = {
    b: unknown;
    a: unknown;
};

/**
 * Delta object: dot-path → { b, a }. Empty object means "no changes".
 * Path components are joined with `.`; array indices appear as bare integers,
 * e.g. `"tradeIns.items.0.amountOwed"`.
 */
export type StateDelta = Record<string, FieldDelta>;

/**
 * Maximum recursion depth when diffing nested objects. Guards against cyclic
 * refs and pathologically deep trees. Beyond this depth, the whole subtree is
 * emitted as a single `{ b, a }` pair instead of drilling further.
 */
const MAX_DIFF_DEPTH = 12;

/**
 * Compute a per-field delta between two snapshot objects.
 *
 * Both `before` and `after` are expected to be the output of
 * {@link pickAllSnapshotSlices} — i.e. plain JSON-shaped objects, no functions
 * or class instances. Referential equality short-circuits the recursion.
 *
 * Behavior:
 *   - Fields present in `after` but not `before`: emitted as `{ b: undefined, a: <value> }`.
 *   - Fields present in `before` but not `after`: emitted as `{ b: <value>, a: undefined }`.
 *   - Different primitives / arrays of different length: emitted as `{ b, a }`.
 *   - Deeply-equal subtrees: skipped.
 *
 * The result contains ONLY changed fields — an unchanged snapshot yields `{}`.
 */
export function computeDelta(before: unknown, after: unknown): StateDelta {
    const delta: StateDelta = {};
    diffInto(before, after, '', delta, 0);
    return delta;
}

function diffInto(before: unknown, after: unknown, path: string, out: StateDelta, depth: number): void {
    if (before === after) return;

    // Depth guard: emit the whole subtree as one entry and stop drilling.
    if (depth >= MAX_DIFF_DEPTH) {
        if (path.length > 0) out[path] = { b: before, a: after };
        return;
    }

    const bIsObj = isPlainDiffable(before);
    const aIsObj = isPlainDiffable(after);

    // Type change or one side is a primitive/array-vs-object: emit whole subtree.
    if (!bIsObj || !aIsObj) {
        if (path.length > 0) out[path] = { b: before, a: after };
        // Root-level primitive change is a degenerate case — we emit at root
        // with the empty key. Middleware callers should never diff two roots
        // that aren't objects, but handle it gracefully anyway.
        else out[''] = { b: before, a: after };
        return;
    }

    // Both sides are plain objects OR arrays — recurse per-key/index.
    const bothArrays = Array.isArray(before) && Array.isArray(after);
    if (bothArrays) {
        const bArr = before as unknown[];
        const aArr = after as unknown[];
        if (bArr.length !== aArr.length) {
            // Length change: emit the whole array as one entry. Trying to align
            // items would produce misleading per-index diffs on insertions.
            out[path || '[]'] = { b: bArr, a: aArr };
            return;
        }
        for (let i = 0; i < bArr.length; i++) {
            const childPath = path.length > 0 ? `${path}.${i}` : String(i);
            diffInto(bArr[i], aArr[i], childPath, out, depth + 1);
        }
        return;
    }

    // Type mismatch (one array, one object) — fall back to whole-subtree emit.
    if (Array.isArray(before) !== Array.isArray(after)) {
        out[path] = { b: before, a: after };
        return;
    }

    const bObj = before as Record<string, unknown>;
    const aObj = after as Record<string, unknown>;
    // Union of keys — visited in a stable order for deterministic output.
    const keys = new Set<string>();
    for (const k of Object.keys(bObj)) keys.add(k);
    for (const k of Object.keys(aObj)) keys.add(k);
    const sortedKeys = Array.from(keys).sort();
    for (const key of sortedKeys) {
        const childPath = path.length > 0 ? `${path}.${key}` : key;
        diffInto(bObj[key], aObj[key], childPath, out, depth + 1);
    }
}

/**
 * A "plain diffable" value is a non-null object or array — something we can
 * recurse into. Everything else (primitives, null, undefined, functions,
 * dates, class instances) is treated as an opaque leaf.
 */
function isPlainDiffable(v: unknown): v is Record<string, unknown> | unknown[] {
    if (v === null || v === undefined) return false;
    if (typeof v !== 'object') return false;
    if (Array.isArray(v)) return true;
    // Plain object heuristic: prototype is Object.prototype or null.
    const proto = Object.getPrototypeOf(v);
    return proto === Object.prototype || proto === null;
}

/**
 * Byte-size estimator for a snapshot / delta payload. Uses JSON serialization
 * length (utf-16 chars ≈ bytes for typical ASCII payloads; underestimates for
 * multibyte). Used by the middleware to enforce the byte budget and by tests
 * to assert size expectations.
 *
 * Returns 0 if the value cannot be serialized (e.g. circular refs).
 */
export function estimatePayloadBytes(value: unknown): number {
    try {
        return JSON.stringify(value)?.length ?? 0;
    } catch {
        return 0;
    }
}
